import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../config';

const About = () => {
  const [leadershipTeam, setLeadershipTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Disable right-click context menu on the entire page
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag and drop for images
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection for images
    const handleSelectStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    // Disable keyboard shortcuts that could be used to save images
    const handleKeyDown = (e) => {
      // Disable Ctrl+S, Ctrl+A, F12, Ctrl+Shift+I, Ctrl+U
      if (
        (e.ctrlKey && (e.key === 's' || e.key === 'a' || e.key === 'u')) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I')
      ) {
        e.preventDefault();
        // Show custom alert for developer tools attempts
        if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
          alert('Developer tools are disabled to protect content integrity.');
        }
        return false;
      }
    };

    // Detect developer tools opening
    const detectDevTools = () => {
      const threshold = 160;
      setInterval(() => {
        if (
          window.outerHeight - window.innerHeight > threshold ||
          window.outerWidth - window.innerWidth > threshold
        ) {
          alert('Please respect our content protection policies.');
        }
      }, 500);
    };

    // Start dev tools detection
    detectDevTools();

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    // SEO: Update page title and meta description
    document.title = 'About KPCA - Kenya Professional Cleaners Association';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about Kenya Professional Cleaners Association (KPCA) - our mission, vision, history, and commitment to professionalizing the cleaning industry in Kenya.');
    }

    // Fetch leadership team data
    fetchLeadershipTeam();
  }, []);

  const fetchLeadershipTeam = async () => {
    try {
      const response = await fetch(`${config.API_URL}/manage-site/about/public`);
      const data = await response.json();

      if (data.success && data.data.leadership) {
        setLeadershipTeam(data.data.leadership);
      } else {
        console.log('API Response:', data);
      }
    } catch (error) {
      console.error('Error fetching leadership team:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to get initials from name
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      {/* Additional CSS protection for images */}
      <style jsx>{`
        img {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-user-drag: none !important;
          -webkit-touch-callout: none !important;
          pointer-events: none !important;
        }
        
        /* Disable print styling for images */
        @media print {
          img {
            display: none !important;
          }
        }
        
        /* Additional protection */
        * {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -khtml-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        
        /* Allow text selection for readable content */
        p, span, h1, h2, h3, h4, h5, h6, div {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
        }
      `}</style>

      <main className="min-h-screen">
        {/* Enhanced Hero Section */}
        <section className="relative h-auto flex items-start justify-center overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-gray-900 pt-2.5 pb-5">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_50%,rgba(59,130,246,0.3),transparent_70%)]"></div>
            <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_20%,rgba(249,115,22,0.3),transparent_70%)]"></div>
          </div>

          {/* Floating Geometric Shapes */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/5 rounded-full backdrop-blur-sm animate-pulse-slow"></div>
            <div className="absolute top-3/4 right-1/4 w-24 h-24 bg-orange-400/10 rounded-full backdrop-blur-sm animate-pulse-slow animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/6 w-16 h-16 bg-blue-400/10 rounded-full backdrop-blur-sm animate-pulse-slow animation-delay-1000"></div>
            <div className="absolute bottom-1/4 right-1/3 w-20 h-20 bg-yellow-400/5 rounded-full backdrop-blur-sm animate-pulse-slow animation-delay-3000"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
            <div className="animate-fade-in-up">
              {/* Premium Badge */}
              <div className="inline-flex items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-2 mb-8 text-sm font-semibold text-white glass-effect">
                <svg className="w-4 h-4 mr-2 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Kenya's Premier Professional Cleaning Association
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-tight mb-4">
                <span className="text-shadow-lg">About </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-400 to-yellow-300 text-shadow-lg animate-gradient-x">KPCA</span>
              </h1>



              <p className="text-xl sm:text-2xl text-blue-100 font-light leading-relaxed max-w-4xl mx-auto mb-12">
                <span className="block mb-2">Elevating Standards • Empowering Professionals</span>
                <span className="text-lg text-blue-200">Transforming Kenya's Cleaning Industry Since <span className="font-semibold text-yellow-300">2010</span></span>
              </p>

              {/* Enhanced Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
                <div className="glass-effect rounded-2xl p-6 hover-glow border border-white/10 backdrop-blur-md">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-300 mb-2">500+</div>
                  <div className="text-sm text-blue-200">Active Members</div>
                </div>
                <div className="glass-effect rounded-2xl p-6 hover-glow border border-white/10 backdrop-blur-md">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-300 mb-2">2K+</div>
                  <div className="text-sm text-blue-200">Trained Professionals</div>
                </div>
                <div className="glass-effect rounded-2xl p-6 hover-glow border border-white/10 backdrop-blur-md">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-300 mb-2">300+</div>
                  <div className="text-sm text-blue-200">Certified Businesses</div>
                </div>
                <div className="glass-effect rounded-2xl p-6 hover-glow border border-white/10 backdrop-blur-md">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-300 mb-2">15+</div>
                  <div className="text-sm text-blue-200">Years Excellence</div>
                </div>
              </div>

              {/* Enhanced CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link
                  to="/register"
                  className="group relative bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 rounded-2xl text-lg font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/25 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative flex items-center justify-center">
                    Join Our Community
                    <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Link>

                <Link
                  to="/services"
                  className="group bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 transform hover:scale-105 backdrop-blur-md glass-effect"
                >
                  <span className="flex items-center justify-center">
                    Our Services
                    <svg className="w-5 h-5 ml-2 transform group-hover:rotate-45 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-gray-50 to-transparent"></div>
        </section>

        {/* Mission, Vision, Values */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-gray-700 leading-relaxed">
                  To professionalize the cleaning industry in Kenya by providing certification,
                  standards, and establishing quality that ensures excellence in service delivery.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-gray-700 leading-relaxed">
                  To be the leading authority in professional cleaning standards in Kenya,
                  recognized for transforming the industry through innovation and education.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h2>
                <p className="text-gray-700 leading-relaxed">
                  Excellence, Integrity, Innovation, Sustainability, and Community -
                  driving our commitment to elevating the cleaning industry.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Our Story
                </h2>
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p>
                    Founded in 2010, the Kenya Professional Cleaners Association (KPCA) emerged
                    from a vision to transform the cleaning industry in Kenya. What started as
                    a small group of dedicated cleaning professionals has grown into the country's
                    premier association for cleaning excellence.
                  </p>
                  <p>
                    Our founders recognized that the cleaning industry lacked professional
                    standards, proper quality programs, and industry recognition. They set
                    out to change this by establishing KPCA as a beacon of professionalism
                    and excellence.
                  </p>
                  <p>
                    Today, we proudly serve over 500 members across Kenya, having trained
                    thousands of cleaning professionals and certified hundreds of cleaning
                    businesses to meet international standards.
                  </p>
                </div>
              </div>
              <div className="order-first lg:order-last">
                <div className="bg-primary-600 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-6">By the Numbers</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-300 mb-2">500+</div>
                      <div className="text-sm text-blue-200">Active Members</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-300 mb-2">2,000+</div>
                      <div className="text-sm text-blue-200">Professionals Trained</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-300 mb-2">300+</div>
                      <div className="text-sm text-blue-200">Certified Businesses</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-300 mb-2">15+</div>
                      <div className="text-sm text-blue-200">Years of Excellence</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Leadership Team
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Meet the dedicated professionals leading KPCA's mission to transform
                the cleaning industry in Kenya
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {loading ? (
                // Loading skeleton
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center animate-pulse">
                    <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-3 w-1/2 mx-auto"></div>
                    <div className="h-4 bg-gray-200 rounded mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                  </div>
                ))
              ) : leadershipTeam.length > 0 ? (
                // Dynamic leadership team
                leadershipTeam.map((member) => (
                  <div key={member.id} className="bg-white rounded-xl shadow-lg p-6 text-center">
                    <div className="w-24 h-24 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
                      {member.portrait_image ? (
                        <img
                          src={`${config.API_URL.replace('/api', '/')}${member.portrait_image}`}
                          alt={`${member.name} - ${member.position}`}
                          className="w-full h-full object-cover select-none pointer-events-none"
                          style={{
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            WebkitUserDrag: 'none',
                            WebkitTouchCallout: 'none'
                          }}
                          draggable={false}
                          onContextMenu={(e) => e.preventDefault()}
                          onDragStart={(e) => e.preventDefault()}
                          onSelectStart={(e) => e.preventDefault()}
                          onError={(e) => {
                            // Fallback to initials if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span
                        className={`text-white font-bold text-2xl ${member.portrait_image ? 'hidden' : 'flex items-center justify-center'}`}
                        style={{ display: member.portrait_image ? 'none' : 'flex' }}
                      >
                        {getInitials(member.name)}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-primary-600 font-medium mb-3">{member.position}</p>
                    <p className="text-gray-600 text-sm">
                      {member.bio}
                    </p>
                  </div>
                ))
              ) : (
                // Fallback content if no data
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-500">Leadership team information is currently being updated.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Why Choose KPCA */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose KPCA?
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                We're more than just an association - we're your partner in professional growth
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Recognized Standards</h3>
                <p className="text-gray-600 text-sm">
                  Our certification is recognized industry-wide and trusted by clients
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Comprehensive Standards</h3>
                <p className="text-gray-600 text-sm">
                  From basic skills to advanced techniques and business management
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Strong Network</h3>
                <p className="text-gray-600 text-sm">
                  Connect with professionals and access exclusive opportunities
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ongoing Support</h3>
                <p className="text-gray-600 text-sm">
                  Continuous guidance and resources for your professional journey
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Join Our Community?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Become part of Kenya's leading professional cleaning association.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-200"
              >
                Join KPCA Today
              </Link>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-200"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default About; 